import {
  ActionIcon,
  Box,
  Button,
  CloseButton,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Title,
  TypographyStylesProvider,
} from '@mantine/core';
import { IconSend } from '@tabler/icons-react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Form } from '@/api';
import { useRustyState } from '../../state';

const PANEL_WIDTH = 380;

export interface ChatAssistantProps {
  /** The form as currently saved; its settings go along with the question. */
  form: Form;
  opened: boolean;
  onToggle: () => void;
}

// The settings the assistant answers from; the form's id travels in the URL.
function formState(form: Form): Record<string, unknown> {
  return {
    title: form.title,
    method: form.method,
    filter_spam: form.filter_spam,
    check_challenge: form.check_challenge,
    challenge_algorithm: form.challenge_algorithm,
    redirect_url: form.redirect_url,
    check_specs: form.check_specs,
    specs: form.specs,
    auto_response_enabled: form.auto_response_enabled,
    auto_response_subject: form.auto_response_subject,
    auto_response_text: form.auto_response_text,
    email_notification_format: form.email_notification_format,
    auto_response_format: form.auto_response_format,
    retention_days: form.retention_days ?? null,
    allowed_origins: form.allowed_origins ?? [],
    encryption_mode: form.encryption_mode,
    email_notification_type: form.email_notification_type,
  };
}

export function ChatAssistant({ form, opened, onToggle }: ChatAssistantProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: follows the answer as it streams in
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [answer]);

  useEffect(() => {
    if (opened && !question && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [opened, question]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isStreaming) return;

    const currentInput = input.trim();
    setQuestion(currentInput);
    setInput('');
    setAnswer('');
    setError(null);
    setIsStreaming(true);

    const api = useRustyState.getState().api;

    await api.formChat(
      form.id,
      currentInput,
      formState(form),
      (content: string) => {
        setAnswer((prev) => prev + content);
      },
      () => {
        setIsStreaming(false);
      },
      (errorType: string) => {
        setIsStreaming(false);
        setError(errorType);
      }
    );
  }, [input, isStreaming, form]);

  const handleNewQuestion = useCallback(() => {
    setQuestion('');
    setAnswer('');
    setError(null);
    setInput('');
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const answerHtml = useMemo(() => {
    if (!answer) return '';
    const raw = marked.parse(answer, { async: false }) as string;
    return DOMPurify.sanitize(raw);
  }, [answer]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  if (!opened) {
    return null;
  }

  return (
    <Paper
      shadow="sm"
      withBorder
      component="aside"
      aria-label={t('form.assistant.title')}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === 'Escape' && !e.defaultPrevented) onToggle();
      }}
      style={{
        width: PANEL_WIDTH,
        minWidth: PANEL_WIDTH,
        height: 'calc(100dvh - 160px)',
        position: 'sticky',
        top: 76,
        alignSelf: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Group
        justify="space-between"
        p="sm"
        style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
      >
        <Title order={5}>{t('form.assistant.title')}</Title>
        <CloseButton onClick={onToggle} aria-label={t('ui.close')} />
      </Group>
      <Text size="xs" c="dimmed" px="sm" pt="xs">
        {t('form.assistant.external')}
      </Text>

      <ScrollArea style={{ flex: 1 }} viewportRef={scrollRef} p="sm">
        {question && (
          <Text size="sm" c="dimmed" mb="xs" fs="italic">
            {question}
          </Text>
        )}
        {answer && (
          <TypographyStylesProvider fz="sm">
            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: DOMPurify-sanitised markdown */}
            <div dangerouslySetInnerHTML={{ __html: answerHtml }} />
          </TypographyStylesProvider>
        )}
        {error && (
          <Text size="sm" c="red">
            {error === 'rateLimit' ? t('chat.rateLimit') : t('chat.error')}
          </Text>
        )}
        {isStreaming && !answer && (
          <Group gap="xs">
            <Loader size="xs" type="dots" />
            <Text size="sm" c="dimmed">
              {t('chat.thinking')}
            </Text>
          </Group>
        )}
        {!question && !error && (
          <Text size="sm" c="dimmed">
            {t('chat.placeholder')}
          </Text>
        )}
      </ScrollArea>

      <Box p="sm" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
        <Stack gap="xs">
          {(answer || error) && !isStreaming && (
            <Button variant="light" size="xs" fullWidth onClick={handleNewQuestion}>
              {t('chat.newQuestion')}
            </Button>
          )}
          {!question && (
            <Group gap="xs" align="flex-end">
              <Textarea
                ref={inputRef}
                placeholder={t('chat.placeholder')}
                value={input}
                onChange={(e) => setInput(e.currentTarget.value)}
                onKeyDown={handleKeyDown}
                autosize
                minRows={1}
                maxRows={4}
                style={{ flex: 1 }}
                disabled={isStreaming}
              />
              <ActionIcon
                size="lg"
                variant="filled"
                onClick={handleSend}
                disabled={!input.trim() || isStreaming}
              >
                <IconSend size={18} />
              </ActionIcon>
            </Group>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}
